import React, {
  Component,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { MapContext as MapContextProvider, MapCanvas } from '@sw-sw/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery } from 'react-responsive';

import { Header } from './Header';
import Toolbar from './Toolbar';
import PositionableDataSource from './Positionable/DataSource';
import DataSourceContext from './DataSourceContext';
import PositionableNavigation from './Positionable/Navigation';
import InteractionContext from './Interaction/InteractionContext';
import PositionableInstanceDataContext from './Positionable/InstanceDataContext';
import DataSourceFeatures from './DataSourceFeatures';
import RolesContext from '../../contexts/RolesContext';
import { MapEditorRouteContext } from '../CustomRoutes/MapEditor';
import { useMap } from './useMap';
import { CalibrateMapProvider } from './CalibrateMap/CalibrateMap';
import GISCalibration from '../Map/GISCalibration';
import GISConfigure from '../Map/GISConfigure';
import MapboxView from '../Map/MapboxView';
import projectApi from '../../utils/api/project';
import EsriFeaturesLayer from './EsriFeaturesLayer';
import ImageLayer from 'ol/layer/Image';
// Single source of truth for OL pixel ↔ geo (y-up, origin bottom-left).
import { pixelToLatLng as bilinearPixelToGeo } from '../../utils/geo';

const MapEditorContext = React.createContext({});

/** prop-less ui for MapEditor */
const MapEditorUI = ({ mapId, divisionId, projectId }) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const [sidebarState, setSidebarState] = useState(false);
  const isTabletOrLess = useMediaQuery({ maxWidth: 768 });
  const {
    mapMobileView,
    showGeoReference,
    setShowGeoReference,
    geoRefSaveRef,
    setGeoRefReady,
    gisEnabled,
    gisCalibrated,
    project,
    projectUpload,
  } = useContext(MapEditorRouteContext);
  const [dropMenu, setDropMenu] = useState(false);
  const mapboxBgRef = useRef(null);
  const olMap = useMap();
  const [mapboxSynced, setMapboxSynced] = useState(false);
  const [mapboxOpacity, setMapboxOpacity] = useState(0.3); // 30% on initial load

  // ESRI Refresh button — only relevant for gis_esri / gis_image_esri site maps
  const isEsriMapType =
    projectUpload?.site_map_type === 'gis_esri' ||
    projectUpload?.site_map_type === 'gis_image_esri';
  // gis_image_esri requires georef before refresh; gis_esri is georef'd inline
  const esriRefreshDisabled =
    !isEsriMapType ||
    (projectUpload?.site_map_type === 'gis_image_esri' && !gisCalibrated);
  // gis_only / gis_esri use a satellite screenshot as their OL base image,
  // which duplicates the live satellite overlay.
  const isSatelliteScreenshotMap =
    projectUpload?.site_map_type === 'gis_only' ||
    projectUpload?.site_map_type === 'gis_esri';
  const [esriRefreshing, setEsriRefreshing] = useState(false);
  const [esriRefreshError, setEsriRefreshError] = useState(null);
  const [esriRefreshTick, setEsriRefreshTick] = useState(0);
  const [esriVisible, setEsriVisible] = useState(true);

  const handleEsriRefresh = useCallback(async () => {
    if (!projectUpload?.id || !project?.id) return;
    setEsriRefreshing(true);
    setEsriRefreshError(null);
    try {
      await projectApi.esriPositionables.refresh(project.id, projectUpload.id);
      setEsriRefreshTick((t) => t + 1);
    } catch (err) {
      setEsriRefreshError(err?.response?.data?.error || 'Refresh failed');
    } finally {
      setEsriRefreshing(false);
    }
  }, [project?.id, projectUpload?.id]);

  const showMapboxBackground = gisEnabled && gisCalibrated;

  // The map view stays mounted (just hidden) while in GeoReference mode, so its
  // feature layers survive — destroying/rebuilding the OL map drops them because
  // features are only drawn onto a layer on mount. When we come back, the
  // container was display:none so OL measured it at 0; recompute its size so the
  // image and all placed legend items/findings render again.
  useEffect(() => {
    if (!showGeoReference && olMap) {
      olMap.updateSize();
    }
  }, [showGeoReference, olMap]);

  // OL uses a pixel-based projection so its view coordinates are in image-pixel
  // space, not geographic. We map the four OL viewport corners to [lng, lat] via
  // bilinear interpolation over the calibrated corners, then fit Mapbox to that
  // geographic bounding box — keeping the satellite map locked to the site image.
  const syncMapboxWithOL = useCallback(() => {
    const mbMap = mapboxBgRef.current?.getMap();
    if (!mbMap || !olMap) return;

    const view = olMap.getView();
    const center = view.getCenter();
    const resolution = view.getResolution();
    const size = olMap.getSize();
    if (!center || resolution == null || !size) return;

    const extent = view.getProjection().getExtent(); // [0, 0, imgW, imgH]
    const imgW = extent[2];
    const imgH = extent[3];
    if (!imgW || !imgH) return;

    const pu = projectUpload;
    if (pu?.geo_top_left_lat == null) return; // not yet calibrated

    // Four corners of the current OL viewport in image-pixel space
    const [W, H] = size;
    const halfW = (W / 2) * resolution;
    const halfH = (H / 2) * resolution;
    const [cx, cy] = center;
    const pixCorners = [
      [cx - halfW, cy + halfH], // viewport top-left
      [cx + halfW, cy + halfH], // viewport top-right
      [cx - halfW, cy - halfH], // viewport bottom-left
      [cx + halfW, cy - halfH], // viewport bottom-right
    ];

    const geoCorners = pixCorners.map(([px, py]) =>
      bilinearPixelToGeo(px, py, imgW, imgH, pu),
    );

    const lngs = geoCorners.map((g) => g[0]);
    const lats = geoCorners.map((g) => g[1]);
    const sw = [Math.min(...lngs), Math.min(...lats)];
    const ne = [Math.max(...lngs), Math.max(...lats)];

    const camera = mbMap.cameraForBounds([sw, ne], { padding: 0 });
    if (camera) {
      mbMap.jumpTo(camera);
      setMapboxSynced(true);
    }
  }, [olMap, projectUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  // change:center fires on every drag pointer-move and every animation frame,
  // so Mapbox stays in sync with the OL image in real time.
  useEffect(() => {
    if (!showMapboxBackground || !olMap) return;

    const view = olMap.getView();
    view.on('change:center', syncMapboxWithOL);
    view.on('change:resolution', syncMapboxWithOL);

    // Initial sync — covers the case where Mapbox loaded before OL was ready
    syncMapboxWithOL();

    return () => {
      view.un('change:center', syncMapboxWithOL);
      view.un('change:resolution', syncMapboxWithOL);
    };
  }, [showMapboxBackground, olMap, syncMapboxWithOL]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMapboxBgLoad = useCallback(() => {
    syncMapboxWithOL();
  }, [syncMapboxWithOL]);

  // Satellite-bg pan limit: image's calibrated geo box expanded by 2x its own
  // span on each side, so the user can drift at most 2 image-widths outward.
  const satelliteBgBounds = useMemo(() => {
    const pu = projectUpload;
    if (pu?.geo_top_left_lat == null) return null;
    const lngs = [
      pu.geo_top_left_lng,
      pu.geo_top_right_lng,
      pu.geo_bottom_left_lng,
      pu.geo_bottom_right_lng,
    ];
    const lats = [
      pu.geo_top_left_lat,
      pu.geo_top_right_lat,
      pu.geo_bottom_left_lat,
      pu.geo_bottom_right_lat,
    ];
    const west = Math.min(...lngs),
      east = Math.max(...lngs);
    const south = Math.min(...lats),
      north = Math.max(...lats);
    const dLng = (east - west) * 1.2,
      dLat = (north - south) * 1.2;
    return {
      west: west - dLng,
      east: east + dLng,
      south: south - dLat,
      north: north + dLat,
    };
  }, [projectUpload]);

  // Lock OL minZoom to the initial fit-zoom so the user can't shrink the
  // image past the level where it just fills the viewport.
  useEffect(() => {
    if (!olMap) return;
    const view = olMap.getView();
    const z = view.getZoom();
    if (Number.isFinite(z)) view.setMinZoom(z - 0.2);
  }, [olMap]);

  // Live coordinate tooltip that follows the cursor on a georeferenced map,
  // mirroring the georeference-mode tooltip. Updated via DOM ref to avoid
  // re-rendering on every pointer move.
  const coordTooltipRef = useRef(null);

  useEffect(() => {
    const pu = projectUpload;

    if (
      showGeoReference ||
      !olMap ||
      !gisEnabled ||
      !gisCalibrated ||
      pu?.geo_top_left_lat == null
    ) {
      return undefined;
    }

    const [, , imgW, imgH] = olMap.getView().getProjection().getExtent();

    const showCoord = (evt) => {
      const el = coordTooltipRef.current;

      if (!el || !imgW || !imgH) return;

      const [lng, lat] = bilinearPixelToGeo(
        evt.coordinate[0],
        evt.coordinate[1],
        imgW,
        imgH,
        pu,
      );

      el.textContent = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      el.style.display = 'block';

      const [vw, vh] = olMap.getSize() || [0, 0];
      const [px, py] = evt.pixel;
      const left = px + 14 + el.offsetWidth > vw ? px - 14 - el.offsetWidth : px + 14;
      const top = py + 14 + el.offsetHeight > vh ? py - 14 - el.offsetHeight : py + 14;

      el.style.left = `${Math.max(0, left)}px`;
      el.style.top = `${Math.max(0, top)}px`;
    };
    const hideCoord = () => {
      if (coordTooltipRef.current) {
        coordTooltipRef.current.style.display = 'none';
      }
    };

    olMap.on('pointermove', showCoord);
    olMap.getViewport().addEventListener('pointerleave', hideCoord);

    return () => {
      olMap.un('pointermove', showCoord);
      olMap.getViewport().removeEventListener('pointerleave', hideCoord);
    };
  }, [olMap, gisEnabled, gisCalibrated, showGeoReference, projectUpload]);

  return (
    <div
      className={
        mapMobileView ? 'map-editor map-editor-mobile-view' : 'map-editor'
      }
    >
      <div className='map-editor-top'>
        <Header />
        <Toolbar
          permCheck={permCheck}
          mapId={mapId}
          divisionId={divisionId}
          projectId={projectId}
        />
      </div>

      {/* GeoReference mode — full-screen calibration view. The normal map view
          below stays mounted (just hidden) so the OL map and its feature layers
          are never destroyed; cancelling reveals them again with no data loss. */}
      {showGeoReference && (
        <div
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {projectUpload?.site_map_type === 'gis_only' ||
          projectUpload?.site_map_type === 'gis_esri' ? (
            <GISConfigure
              ref={(r) => {
                if (geoRefSaveRef) geoRefSaveRef.current = r?.save ?? null;
              }}
              embedded
              projectId={projectId}
              projectUpload={projectUpload}
              project={project}
              onReadyChange={setGeoRefReady}
              bounds={
                project?.bounds_west != null && project?.bounds_east != null
                  ? {
                      west: project.bounds_west,
                      east: project.bounds_east,
                      south: project.bounds_south,
                      north: project.bounds_north,
                    }
                  : null
              }
              onSaved={(linked) => {
                setShowGeoReference && setShowGeoReference(false);
                const newGuid = linked?.upload?.GUID;
                if (newGuid) {
                  window.location.href =
                    window.location.pathname.replace(
                      /\/map\/[^/]+\/edit/,
                      `/map/${newGuid}/edit`,
                    ) + window.location.search;
                } else {
                  window.location.reload();
                }
              }}
              onCancel={() => setShowGeoReference && setShowGeoReference(false)}
            />
          ) : (
            <GISCalibration
              ref={(r) => {
                if (geoRefSaveRef) geoRefSaveRef.current = r?.save ?? null;
              }}
              project={project}
              projectUpload={projectUpload}
              onReadyChange={setGeoRefReady}
              bounds={
                project?.bounds_west != null && project?.bounds_east != null
                  ? {
                      west: project.bounds_west,
                      east: project.bounds_east,
                      south: project.bounds_south,
                      north: project.bounds_north,
                    }
                  : null
              }
              onSaved={() => {
                setShowGeoReference && setShowGeoReference(false);
                window.location.reload();
              }}
              onCancel={() => setShowGeoReference && setShowGeoReference(false)}
            />
          )}
        </div>
      )}

      <div
        className='pure-g map-editor-main'
        style={showGeoReference ? { display: 'none' } : undefined}
      >
        {sidebarState && (isTabletOrLess || mapMobileView) ? (
          <></>
        ) : (
          <div
            className={`pure-u pure-u-1-3 pure-u-md-1-4 pure-u-lg-1-6 map-editor-sidebar ${
              dropMenu ? 'map-editor-sidebar-change' : ''
            }`}
          >
            <PositionableNavigation
              permCheck={permCheck}
              dropMenu={setDropMenu}
            />
          </div>
        )}

        <div
          className='pure-u pure-u-2-3 pure-u-md-3-4 pure-u-lg-5-6 map-editor-map'
          style={showMapboxBackground ? { position: 'relative' } : undefined}
        >
          {/* OL site-map — receives all pointer events. For satellite-screenshot
              maps it sits above the satellite so placed items stay visible. */}
          <div
            style={{
              height: '100%',
              position: 'relative',
              ...(showMapboxBackground && isSatelliteScreenshotMap
                ? { zIndex: 2 }
                : {}),
            }}
          >
            <MapEditorContext.Consumer>
              {({ mapImgSrc, error }) =>
                error ? (
                  <p className='error'>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className='alert'
                    />{' '}
                    {error}
                  </p>
                ) : (
                  <MapCanvas
                    mapImgSrc={mapImgSrc}
                    styles={
                      showMapboxBackground && isSatelliteScreenshotMap
                        ? { background: 'none' }
                        : undefined
                    }
                  />
                )
              }
            </MapEditorContext.Consumer>
            {showMapboxBackground && isEsriMapType && (
              <EsriFeaturesLayer
                projectId={project?.id}
                projectUpload={projectUpload}
                refreshTick={esriRefreshTick}
                hidden={!esriVisible}
              />
            )}

            {/* Live coordinate tooltip — DOM ref, no React re-renders. */}
            <div
              ref={coordTooltipRef}
              style={{
                display: 'none',
                position: 'absolute',
                zIndex: 20,
                background: 'rgba(0,0,0,0.65)',
                color: '#fff',
                fontSize: 11,
                fontFamily: 'monospace',
                padding: '3px 7px',
                borderRadius: 4,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                lineHeight: 1.5,
              }}
            />
          </div>

          <div
            className='map-editor-hamburger-icon'
            onClick={() => setSidebarState(!sidebarState)}
          >
            <FontAwesomeIcon
              className='map-editor-hamburger-svg'
              icon={sidebarState ? faBars : faTimes}
              size='xs'
            />
          </div>

          {showMapboxBackground && (
            <>
              {/* Satellite overlay — on top of the OL map, pointer-events:none so
                  the OL map below still handles pan/zoom interactions */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: isSatelliteScreenshotMap ? 1 : 2,
                  opacity: mapboxSynced ? mapboxOpacity : 0,
                  pointerEvents: 'none',
                  transition: 'opacity 0.25s',
                }}
              >
                <MapboxView
                  ref={mapboxBgRef}
                  onMapLoad={handleMapboxBgLoad}
                  style='mapbox://styles/mapbox/satellite-streets-v12'
                  hideControls
                  maxBounds={satelliteBgBounds}
                  maxBoundsPaddingKm={0}
                />
              </div>

              {/* Satellite opacity slider */}
              <div
                style={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  zIndex: 10,
                  background: 'rgba(255,255,255,0.9)',
                  borderRadius: 6,
                  padding: '5px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
                  fontSize: 11,
                  color: '#444',
                }}
              >
                <span>Satellite</span>
                <input
                  type='range'
                  min={0}
                  max={100}
                  value={Math.round(mapboxOpacity * 100)}
                  onChange={(e) =>
                    setMapboxOpacity(Number(e.target.value) / 100)
                  }
                  style={{ width: 80, cursor: 'pointer' }}
                />
                <span style={{ minWidth: 28, textAlign: 'right' }}>
                  {Math.round(mapboxOpacity * 100)}%
                </span>
              </div>
            </>
          )}

          {showMapboxBackground && isEsriMapType && (
            <div
              style={{
                position: 'absolute',
                bottom: showMapboxBackground ? 56 : 12,
                right: 12,
                zIndex: 10,
                background: 'rgba(255,255,255,0.9)',
                borderRadius: 6,
                padding: '0px',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
                fontSize: 11,
                color: '#444',
              }}
            >
              {!(esriRefreshDisabled || esriRefreshing) && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'pointer',
                    padding: '5px 10px',
                  }}
                >
                  <input
                    type='checkbox'
                    checked={esriVisible}
                    onChange={(e) => setEsriVisible(e.target.checked)}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  <span>ESRI</span>
                </label>
              )}

              <button
                type='button'
                onClick={handleEsriRefresh}
                disabled={esriRefreshDisabled || esriRefreshing}
                title={
                  projectUpload?.site_map_type === 'gis_image_esri' &&
                  !gisCalibrated
                    ? 'Complete georeferencing first'
                    : 'Re-fetch ESRI features for this map'
                }
                style={{ padding: '5px 10px', fontSize: 11 }}
              >
                {esriRefreshing ? 'Refreshing...' : 'Refresh ESRI features'}
              </button>
              {esriRefreshError && (
                <span style={{ color: 'red' }}>{esriRefreshError}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

class MapEditor extends Component {
  static propTypes = {
    mapImgSrc: PropTypes.string,

    dataSources: PropTypes.arrayOf(
      PropTypes.shape({
        ...PositionableDataSource.propTypes,
      }).isRequired,
    ).isRequired,
    mapId: PropTypes.any,
    divisionId: PropTypes.number,
    projectId: PropTypes.number,
    projectUploadId: PropTypes.number,
    projectDocTypeId: PropTypes.number,
    mapName: PropTypes.string,
    mapUploadId: PropTypes.number,
    projectUploadOrder: PropTypes.number,
  };

  state = {
    error: null,
    refetchKey: 0,
  };

  onInit = (_x) => {};

  onInitError = (err) => {
    let message = 'Unknown error';

    if (typeof err === 'string') message = err;
    else if (err && err.message) message = err.message;

    console.error('map init error:', err);

    this.setState({
      error: `There was an error loading the map (${message})`,
    });
  };

  handleRefetchDataSources = () => {
    this.setState((prevState) => ({
      refetchKey: prevState.refetchKey + 1,
    }));
  };

  render() {
    return (
      <MapEditorRouteContext.Consumer>
        {(mapEditorRouteContext) => (
          <CalibrateMapProvider
            mapId={this.props.mapId}
            projectId={this.props.projectId}
            projectUploadId={this.props.projectUploadId}
            projectDocTypeId={this.props.projectDocTypeId}
            mapName={this.props.mapName}
            mapUploadId={this.props.mapUploadId}
            projectUploadOrder={this.props.projectUploadOrder}
            onRefetchDataSources={this.handleRefetchDataSources}
            onCloseCalibrateMap={() => {
              if (
                mapEditorRouteContext &&
                mapEditorRouteContext.setShowCalibrateMap
              ) {
                mapEditorRouteContext.setShowCalibrateMap(false);
              }
            }}
          >
            <MapEditorContext.Provider
              value={{
                mapImgSrc: this.props.mapImgSrc,
                error: this.state.error,
              }}
            >
              <DataSourceContext>
                <MapContextProvider
                  mapImgSrc={this.props.mapImgSrc}
                  dataSources={[]}
                  onInit={this.onInit}
                  onInitError={this.onInitError}
                >
                  <PositionableInstanceDataContext>
                    <InteractionContext>
                      <MapEditorUI
                        mapId={this.props.mapId}
                        divisionId={this.props.divisionId}
                        projectId={this.props.projectId}
                      />
                    </InteractionContext>
                    <MapEditorRouteContext.Consumer>
                      {(value) => (
                        <DataSourceFeatures
                          selectedInspection={value.selectedInspection}
                        />
                      )}
                    </MapEditorRouteContext.Consumer>
                  </PositionableInstanceDataContext>
                </MapContextProvider>

                {this.props.dataSources.map((source, i) => (
                  <PositionableDataSource
                    {...source}
                    key={`${i}-${this.state.refetchKey}`}
                  />
                ))}
              </DataSourceContext>
            </MapEditorContext.Provider>
          </CalibrateMapProvider>
        )}
      </MapEditorRouteContext.Consumer>
    );
  }
}

export default MapEditor;
